import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { subscriptionAPI } from '@/lib/api';
import { ArrowLeft, Users, Crown, Image, Video, DollarSign, Loader2, Shield, Gift, UserPlus, Edit, X, Check, MessageSquare, Sparkles, Smile, Activity, Clock, Mail, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Add User Form
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    name: '',
    plan: 'legacy_3',
    image_credits: 50,
    video_credits: 10,
    expiration_date: ''
  });
  const [adding, setAdding] = useState(false);
  
  // Edit User
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Activity tracking
  const [activity, setActivity] = useState(null);
  const [activityDays, setActivityDays] = useState(7);
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      if (!status.is_admin) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
        return;
      }
      setIsAdmin(true);
      await loadData();
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    const [usersData, statsData] = await Promise.all([
      subscriptionAPI.getAdminUsers(),
      subscriptionAPI.getAdminStats()
    ]);
    setUsers(usersData.users || []);
    setStats(statsData);
    
    // Also load activity
    loadActivity();
  };

  const loadActivity = async () => {
    setLoadingActivity(true);
    try {
      const activityData = await subscriptionAPI.getActivity(activityDays);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to load activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleAddUser = async () => {
    if (!addUserForm.email || !addUserForm.expiration_date) {
      toast.error('Email and expiration date are required');
      return;
    }
    
    setAdding(true);
    try {
      const result = await subscriptionAPI.addUser(addUserForm);
      toast.success(`Added ${result.email} with ${result.plan} plan`);
      setShowAddUser(false);
      setAddUserForm({
        email: '',
        name: '',
        plan: 'legacy_3',
        image_credits: 50,
        video_credits: 10,
        expiration_date: ''
      });
      await loadData();
    } catch (error) {
      console.error('Add user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user.user_id);
    setEditForm({
      image_credits: user.image_credits || 0,
      video_credits: user.video_credits || 0,
      plan: user.plan || 'free',
      expiration_date: user.subscription_end ? user.subscription_end.split('T')[0] : ''
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const saveEdit = async (userId) => {
    setSaving(true);
    try {
      const data = {
        user_id: userId,
        image_credits: parseInt(editForm.image_credits),
        video_credits: parseInt(editForm.video_credits),
        plan: editForm.plan,
        expiration_date: editForm.expiration_date || null
      };
      
      await subscriptionAPI.adjustCredits(data);
      toast.success('User updated successfully');
      setEditingUser(null);
      await loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const ADMIN_EMAIL = "computerwork2022@gmail.com";

  const planColors = {
    free: 'bg-gray-500',
    legacy_3: 'bg-emerald-500',
    starter: 'bg-blue-500',
    pro: 'bg-purple-500',
    business: 'bg-amber-500',
    admin: 'bg-red-500'
  };

  const planNames = {
    free: 'FREE',
    legacy_3: 'LEGACY $3',
    starter: 'STARTER',
    pro: 'PRO',
    business: 'BUSINESS',
    admin: 'ADMIN (UNLIMITED)'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="px-6 py-4 bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Shield className="h-7 w-7 text-red-400" />
          <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Quick Access - Admin Tools */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="p-5 bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-green-500/30 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => navigate('/builder?tab=chatgpt')}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/30 rounded-lg">
                <MessageSquare className="h-7 w-7 text-green-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">ChatGPT Assistant</h3>
                <p className="text-sm text-green-200">AI-powered prompt creation help</p>
              </div>
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                Open
              </Button>
            </div>
          </Card>
          
          <Card 
            className="p-5 bg-gradient-to-br from-purple-500/20 to-pink-600/20 border border-purple-500/30 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => navigate('/builder')}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/30 rounded-lg">
                <Sparkles className="h-7 w-7 text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">Prompt Builder</h3>
                <p className="text-sm text-purple-200">Build perfect AI prompts</p>
              </div>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                Open
              </Button>
            </div>
          </Card>

          <Card 
            className="p-5 bg-gradient-to-br from-yellow-500/20 to-orange-600/20 border border-yellow-500/30 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => navigate('/admin/emoji')}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/30 rounded-lg">
                <Smile className="h-7 w-7 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">Emoji Dictionary</h3>
                <p className="text-sm text-yellow-200">Manage emoji meanings & recipes</p>
              </div>
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                Edit
              </Button>
            </div>
          </Card>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Users className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-blue-200">Total Users</p>
                <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <Gift className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-emerald-200">Legacy $3 Users</p>
                <p className="text-3xl font-bold text-white">{stats?.plan_distribution?.legacy_3 || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Crown className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-purple-200">Paid Users</p>
                <p className="text-3xl font-bold text-white">
                  {(stats?.plan_distribution?.starter || 0) + 
                   (stats?.plan_distribution?.pro || 0) + 
                   (stats?.plan_distribution?.business || 0)}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-green-200">Overage Revenue</p>
                <p className="text-3xl font-bold text-white">${(stats?.total_overage_revenue || 0).toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* User Activity Section */}
        <Card className="p-6 bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-400" />
              User Activity (Last {activityDays} Days)
            </h3>
            <div className="flex items-center gap-2">
              <Select value={activityDays.toString()} onValueChange={(v) => { setActivityDays(parseInt(v)); }}>
                <SelectTrigger className="w-24 bg-white/10 border-orange-500/30 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Day</SelectItem>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadActivity}
                disabled={loadingActivity}
                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/20"
              >
                {loadingActivity ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
          </div>
          
          {activity?.user_summary && activity.user_summary.length > 0 ? (
            <div className="space-y-4">
              {/* Active Users Summary */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activity.user_summary.slice(0, 6).map((userAct, idx) => (
                  <div key={idx} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-orange-400" />
                        <span className="text-white font-medium text-sm truncate max-w-[150px]" title={userAct.email}>
                          {userAct.email}
                        </span>
                      </div>
                      <span className="text-orange-300 text-xs">
                        {userAct.total_actions} actions
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3 text-green-400" />
                        <span className="text-green-300">{userAct.total_credits_used} credits</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-400 text-xs">
                          {new Date(userAct.last_activity).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(userAct.action_breakdown || {}).map(([action, count]) => (
                        <span key={action} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                          {action.replace(/_/g, ' ')}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {activity.user_summary.length === 0 && (
                <p className="text-gray-400 text-center py-4">No activity recorded in the last {activityDays} days</p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-orange-400/30 mx-auto mb-3" />
              <p className="text-gray-400">No recent activity data available</p>
              <p className="text-gray-500 text-sm mt-1">Activity will appear here as users generate images/videos</p>
            </div>
          )}
        </Card>

        {/* Add User Section */}
        <Card className="p-6 bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/30 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-400" />
              Add New Subscriber
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddUser(!showAddUser)}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
            >
              {showAddUser ? 'Cancel' : 'Add User'}
            </Button>
          </div>
          
          {showAddUser && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                <Input 
                  type="email"
                  placeholder="user@example.com"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm({...addUserForm, email: e.target.value})}
                  className="bg-white/10 border-blue-500/30 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Name</label>
                <Input 
                  placeholder="User Name"
                  value={addUserForm.name}
                  onChange={(e) => setAddUserForm({...addUserForm, name: e.target.value})}
                  className="bg-white/10 border-blue-500/30 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Plan</label>
                <Select value={addUserForm.plan} onValueChange={(v) => setAddUserForm({...addUserForm, plan: v})}>
                  <SelectTrigger className="bg-white/10 border-blue-500/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="legacy_3">Legacy $3</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Image Credits</label>
                <Input 
                  type="number"
                  value={addUserForm.image_credits}
                  onChange={(e) => setAddUserForm({...addUserForm, image_credits: parseInt(e.target.value) || 0})}
                  className="bg-white/10 border-blue-500/30 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Video Credits</label>
                <Input 
                  type="number"
                  value={addUserForm.video_credits}
                  onChange={(e) => setAddUserForm({...addUserForm, video_credits: parseInt(e.target.value) || 0})}
                  className="bg-white/10 border-blue-500/30 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Expiration Date *</label>
                <Input 
                  type="date"
                  value={addUserForm.expiration_date}
                  onChange={(e) => setAddUserForm({...addUserForm, expiration_date: e.target.value})}
                  className="bg-white/10 border-blue-500/30 text-white"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Button 
                  onClick={handleAddUser}
                  disabled={adding}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Add Subscriber
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Plan Distribution */}
        <Card className="p-6 bg-white/5 border border-white/10 mb-8">
          <h3 className="text-lg font-bold text-white mb-4">Plan Distribution</h3>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(stats?.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${planColors[plan] || 'bg-gray-500'}`} />
                <span className="text-gray-300">{planNames[plan] || plan.toUpperCase()}: {count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Users Table */}
        <Card className="p-6 bg-white/5 border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4">All Users ({users.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Plan</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-400">Credits</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-400">
                    <Activity className="h-4 w-4 inline mr-1" />Last Active
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Expires</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {user.picture && (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        )}
                        <div>
                          <p className="text-white font-medium">{user.name || 'Pending'}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.email === ADMIN_EMAIL ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white bg-red-500`}>
                          ADMIN (UNLIMITED)
                        </span>
                      ) : editingUser === user.user_id ? (
                        <Select value={editForm.plan} onValueChange={(v) => setEditForm({...editForm, plan: v})}>
                          <SelectTrigger className="w-28 bg-white/10 border-white/20 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="legacy_3">Legacy $3</SelectItem>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${planColors[user.plan] || 'bg-gray-500'}`}>
                          {planNames[user.plan] || user.plan?.toUpperCase() || 'FREE'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {user.email === ADMIN_EMAIL ? (
                        <span className="text-yellow-400 font-bold">∞</span>
                      ) : editingUser === user.user_id ? (
                        <Input 
                          type="number"
                          value={editForm.image_credits}
                          onChange={(e) => setEditForm({...editForm, image_credits: e.target.value})}
                          className="w-20 bg-white/10 border-white/20 text-white text-center text-sm"
                        />
                      ) : (
                        <div>
                          <span className="text-white">{user.credits || user.image_credits || 0}</span>
                          <span className="text-gray-500 text-xs ml-1">credits</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {(() => {
                        // Find last activity for this user
                        const userActivity = activity?.user_summary?.find(a => a.email === user.email);
                        if (user.email === ADMIN_EMAIL) {
                          return <span className="text-yellow-400 text-sm">Always</span>;
                        }
                        if (userActivity?.last_activity) {
                          const lastDate = new Date(userActivity.last_activity);
                          const now = new Date();
                          const diffDays = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
                          return (
                            <div>
                              <span className={`text-sm ${diffDays === 0 ? 'text-green-400' : diffDays < 7 ? 'text-yellow-400' : 'text-gray-400'}`}>
                                {diffDays === 0 ? 'Today' : diffDays === 1 ? 'Yesterday' : `${diffDays}d ago`}
                              </span>
                              <span className="text-gray-500 text-xs block">{userActivity.total_actions} actions</span>
                            </div>
                          );
                        }
                        return <span className="text-gray-500 text-sm">Never</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      {user.email === ADMIN_EMAIL ? (
                        <span className="text-yellow-400 text-sm font-semibold">Never</span>
                      ) : editingUser === user.user_id ? (
                        <Input 
                          type="date"
                          value={editForm.expiration_date}
                          onChange={(e) => setEditForm({...editForm, expiration_date: e.target.value})}
                          className="w-36 bg-white/10 border-white/20 text-white text-sm"
                        />
                      ) : (
                        <span className="text-gray-400 text-sm">
                          {user.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : '-'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {user.email === ADMIN_EMAIL ? (
                        <span className="text-xs text-red-400 font-semibold">ADMIN</span>
                      ) : editingUser === user.user_id ? (
                        <div className="flex gap-2 justify-center">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => saveEdit(user.user_id)}
                            disabled={saving}
                            className="text-green-400 hover:bg-green-500/20"
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={cancelEdit}
                            className="text-red-400 hover:bg-red-500/20"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => startEdit(user)}
                          className="text-blue-400 hover:bg-blue-500/20"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
